import { queryString, type ChartTaxonomyParent, type ChartUnitsSeries } from '../api/client.js';

/**
 * The pure half of the Products cards' sparklines: the batched request (one call
 * per page of cards, at most {@link SPARKLINE_BATCH_MAX} keys) and the geometry of
 * the tiny inline SVG line. Drawn without Recharts so the Products page never
 * loads the chart bundle just for a dozen twelve-point lines.
 */

/** The server's cap on keys per batched series request (SERIES_BATCH_MAX_KEYS). */
export const SPARKLINE_BATCH_MAX = 50;

/** Splits keys into consecutive batches of at most `size`, duplicates dropped. */
export function chunkKeys(keys: readonly string[], size = SPARKLINE_BATCH_MAX): string[][] {
  const unique = [...new Set(keys)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += size) chunks.push(unique.slice(i, i + size));
  return chunks;
}

/**
 * The batched units-series URL for one chunk of series or model keys under one
 * parent. No range: the server defaults to the last 12 calendar months through its
 * business day, so the window never depends on the viewer's clock.
 */
export function sparklineBatchPath(
  scope: 'series' | 'model',
  parent: ChartTaxonomyParent,
  keys: readonly string[],
): string {
  const head = queryString({
    scope,
    category: parent.category,
    series: scope === 'model' ? parent.series : undefined,
  });
  const keyParams = keys.map((k) => `key=${encodeURIComponent(k)}`).join('&');
  return `/api/charts/series/batch${head}${keyParams ? `&${keyParams}` : ''}`;
}

/**
 * The series a sparkline may draw, or undefined when there is nothing true to
 * draw: no entry, or an entry whose key names no SKU (`skuCount` 0 — a series or
 * model renamed by a taxonomy recompute between the Products response and the
 * batch). The batch answers such a key with zero buckets, which would otherwise
 * read as a real flat year; the card shows an em dash instead. Pure.
 */
export function drawableSeries(series: ChartUnitsSeries | undefined): ChartUnitsSeries | undefined {
  return series !== undefined && series.skuCount > 0 ? series : undefined;
}

export interface SparklineGeometry {
  /** The SVG path through every point. */
  line: string;
  /** Every point's position, in order. */
  points: { x: number; y: number }[];
}

/**
 * The polyline through `values` inside a `width` × `height` box with `pad` pixels
 * kept clear on every side (so the stroke and the end dot are never clipped). The
 * y scale runs from zero to the series' own maximum — a sparkline shows shape, and
 * the figure beside it carries the magnitude — and an all-zero series lies flat on
 * the baseline. A single value is drawn as a flat line across the box.
 */
export function sparklineGeometry(
  values: readonly number[],
  width: number,
  height: number,
  pad = 2,
): SparklineGeometry {
  if (values.length === 0) return { line: '', points: [] };
  const max = Math.max(0, ...values);
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const step = values.length > 1 ? innerW / (values.length - 1) : 0;
  const round = (n: number) => Math.round(n * 100) / 100;
  const points = values.map((v, i) => ({
    x: round(pad + (values.length > 1 ? i * step : 0)),
    y: round(pad + innerH - (max > 0 ? (Math.max(0, v) / max) * innerH : 0)),
  }));
  if (points.length === 1) {
    const only = points[0]!;
    return {
      line: `M${round(pad)},${only.y}L${round(width - pad)},${only.y}`,
      points,
    };
  }
  return { line: pathThrough(points), points };
}

/** An SVG path through points in order ('' for none). */
export function pathThrough(points: readonly { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join('');
}
